import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Shield, Video, Zap, FileText, UserCheck,
  CheckCircle2, Clock, Send, BarChart3, BookOpen, Lock
} from "lucide-react";
import { TrustNavigation } from "@/components/TrustNavigation";
import { TrustFooter } from "@/components/TrustFooter";

const STAGES = [
  {
    num: "01",
    title: "Applied",
    desc: "Candidate received from JobAdder. Candidate profile, role, and application auto-created in Vexis from JobAdder data. No manual entry required.",
    icon: BookOpen,
    color: "#6366f1",
  },
  {
    num: "02",
    title: "Link Sent",
    desc: "A unique 72-hour assessment link is dispatched to the candidate by email. A 48-hour reminder is scheduled automatically. Status written back to JobAdder immediately.",
    icon: Send,
    color: "#3b82f6",
  },
  {
    num: "03",
    title: "In Progress",
    desc: "Candidate opens their link on any device. No account or app required. ElevenLabs AI voice narrates each question. Video chunks upload during recording — resilient to connection drops.",
    icon: Video,
    color: "#8b5cf6",
  },
  {
    num: "04",
    title: "Submitted",
    desc: "Candidate completes all questions and submits. Confirmation screen shown. OpenAI Whisper begins transcribing all video responses in parallel. Status written back to JobAdder.",
    icon: CheckCircle2,
    color: "#06b6d4",
  },
  {
    num: "05",
    title: "AI Triage",
    desc: "GPT-4o scores every response against the role criteria using the candidate's JobAdder profile as context. Overall score (0–100), per-question scores, strengths, and gaps generated in under 2 minutes.",
    icon: Zap,
    color: "#f59e0b",
  },
  {
    num: "06",
    title: "Under Review",
    desc: "Your recruiter reviews the full transcript, video responses, and AI scores. This step is mandatory — no outcome can be set without a completed human review. Database-enforced. Cannot be bypassed.",
    icon: UserCheck,
    color: "#10b981",
  },
  {
    num: "07",
    title: "Dossier Ready",
    desc: "Agent completes review. An Executive Talent Dossier is generated automatically and published at a secure link. Score and dossier URL written back to JobAdder. Dossier expires after 14 days.",
    icon: FileText,
    color: "#6366f1",
  },
  {
    num: "08",
    title: "Outcome",
    desc: "Agent records the final outcome — in-person interview or not proceeding. Candidate is notified. JobAdder record updated. Pipeline complete.",
    icon: BarChart3,
    color: "#22c55e",
  },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff" }}>
      <TrustNavigation currentPath="/how-it-works" />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#0a0f1e" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950/60 via-slate-900 to-slate-950" />
        <div className="container mx-auto px-4 py-14 md:py-28 relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.35)", color: "#a5b4fc" }}>
              <Zap className="w-4 h-4" />
              Platform Overview
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              How Vexis Works
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
              From JobAdder to boardroom-ready dossier in under 15 minutes. No phone screens. No scheduling. No guesswork.
            </p>
          </div>
        </div>
      </section>

      {/* Section 1 — The Problem */}
      <section className="py-16 md:py-24" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">The old way is broken.</h2>
            <div className="space-y-5 text-slate-300 text-base md:text-lg leading-relaxed"
              style={{ borderLeft: "3px solid rgba(99,102,241,0.5)", paddingLeft: "1.5rem" }}>
              <p>
                Recruitment agencies are drowning in CVs. In 2026, only 37% of employers still rate CVs as a reliable
                indicator of talent — yet most agencies spend hours video assessing candidates that look identical on paper.
              </p>
              <p>
                The average recruiter spends 6+ hours a week on first-round assessments. Most of those conversations are
                forgotten by Friday.
              </p>
              <p>
                Vexis replaces the unstructured first-round assessment entirely — with a structured, scored, human-verified
                video assessment that gives you everything you need to make a confident recommendation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 — The 8-Stage Pipeline */}
      <section className="py-16 md:py-24" style={{ backgroundColor: "#080b12" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                A structured pipeline. Fully automated. Human-verified.
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Every candidate moves through 8 defined stages. Stages 1–5 are fully automated. Stages 6–8 require your judgment.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {STAGES.map((stage) => {
                const Icon = stage.icon;
                return (
                  <div
                    key={stage.num}
                    className="rounded-xl p-6 flex gap-5"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(99,102,241,0.18)",
                    }}
                  >
                    <div
                      className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: `${stage.color}20`, border: `1px solid ${stage.color}40` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: stage.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold tracking-widest" style={{ color: stage.color }}>
                          STAGE {stage.num}
                        </span>
                      </div>
                      <h3 className="font-semibold text-white mb-2">{stage.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">{stage.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 — The Dossier */}
      <section className="py-16 md:py-24" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">The Executive Talent Dossier.</h2>
            <p className="text-slate-400 mb-8">
              Every completed assessment generates a boardroom-ready dossier your clients can open without logging in.
            </p>
            <div className="rounded-xl p-7 space-y-4"
              style={{ background: "rgba(99,102,241,0.07)", border: "1px solid rgba(99,102,241,0.25)" }}>
              {[
                ["HUMAN VERIFIED badge", "confirms mandatory recruiter review completed"],
                ["Executive Scorecard", "overall score with per-question breakdown"],
                ["AI-generated Executive Summary", "3-sentence narrative from scores and agent notes"],
                ["Professional Profile", "candidate background from JobAdder"],
                ["Role Fit Analysis", "alignment narrative with strengths and gaps"],
                ["Key Strengths", "derived from GPT-4o scoring"],
                ["Interview Recommendations", "4 specific follow-up questions for the in-person stage"],
              ].map(([title, detail]) => (
                <div key={title} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-300 text-sm">
                    <span className="text-white font-medium">{title}</span> — {detail}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-5">
              The dossier is hosted at{" "}
              <span className="text-slate-300 font-medium">dossier.vexistalentagency.com</span>. No login required — the link
              itself is the access token.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4 — JobAdder Integration */}
      <section className="py-16 md:py-24" style={{ backgroundColor: "#080b12" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Built inside JobAdder. Not alongside it.
            </h2>
            <p className="text-slate-300 mb-6 leading-relaxed">
              Vexis connects to JobAdder via OAuth 2.0. Once connected, a 'Vexis Assessment' button appears on every job
              application record.
            </p>
            <p className="text-slate-300 mb-6 leading-relaxed">
              One click opens the Vexis SideDrawer inside JobAdder — no tab switching, no copy-pasting candidate details.
              Everything is pulled from JobAdder automatically.
            </p>
            <p className="text-slate-400 mb-5 text-sm font-medium uppercase tracking-widest">
              Assessment results written back to JobAdder at 4 stages:
            </p>
            <div className="space-y-3">
              {[
                ["Link Sent", "candidate contacted"],
                ["Submitted", "interview complete"],
                ["Dossier Ready", "score + dossier URL written to candidate record"],
                ["Not Proceeding", "record closed"],
              ].map(([stage, detail]) => (
                <div key={stage} className="flex items-center gap-3 text-sm">
                  <ArrowRight className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                  <span className="text-white font-medium">{stage}</span>
                  <span className="text-slate-400">— {detail}</span>
                </div>
              ))}
            </div>
            <p className="text-slate-400 mt-6 text-sm italic">
              Your JobAdder workflow. Your data. Vexis just adds the assessment layer.
            </p>
          </div>
        </div>
      </section>

      {/* Section 5 — Security */}
      <section className="py-16 md:py-24" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Security built in from day one.</h2>
            <p className="text-slate-400 mb-8">
              Vexis is built on enterprise-grade infrastructure:
            </p>
            <div className="space-y-3">
              {[
                "Supabase PostgreSQL with Row Level Security on all tables",
                "AES-256-GCM token encryption at rest",
                "SHA-256 HMAC URL signing on all SideDrawer requests",
                "CSRF protection on all authenticated routes",
                "No candidate data stored client-side",
                "Mandatory human review before any candidate outcome",
                "Compliant with NZ Privacy Act 2020, Human Rights Act 1993, and Employment Relations Act 2000",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <Lock className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-300 text-sm">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 6 — CTA */}
      <section className="py-16 md:py-24" style={{ backgroundColor: "#0a0f1e" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-3">Ready to see it in action?</h2>
            <p className="text-slate-400 mb-8 text-lg">
              Start free. No credit card required. Connect JobAdder in under 3 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/register">
                <Button size="lg" className="h-12 px-8 font-semibold w-full sm:w-auto"
                  style={{ backgroundColor: "#6366f1", color: "#ffffff" }}>
                  Start Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link href="/pricing">
                <Button size="lg" variant="outline"
                  className="h-12 px-8 w-full sm:w-auto border-white/20 text-white hover:bg-white/10">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <TrustFooter />
    </div>
  );
}
